"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import type {
  CNewsArticle,
  CNewsContent,
  CNewsItem,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { cNewsTranslations, type CNewsTranslations } from "./CNews.i18n";
import { ParagraphRenderer } from "@/components/cms/Paragraphs/ParagraphRenderer";
import { CatalogLoadMore } from "@/components/cms/Pagination/CatalogLoadMore";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CNews.module.css";

/* Biblioteka CMS Interakcje (komentarze + reakcje) - ładowana leniwie
   (ssr:false), montuje się i dociąga dane dopiero po wyrenderowaniu artykułu. */
const ArticleInteractions = dynamic(
  () =>
    import("@/components/cms/Interactions/ArticleInteractions").then(
      (m) => m.ArticleInteractions,
    ),
  { ssr: false },
);

/**
 * CNews - visitor view komponentu `c_news` (Aktualności).
 *
 * **Data source:** `C_NEWS_CONTROLLER::getApiContent()` zwraca:
 *   ```
 *   {
 *     id, symbol,
 *     items: [{ id, name, short_text, publication_date, picture, href }, ...],
 *     pagination: { page, per_page, total_count, total_pages }
 *   }
 *   ```
 *
 * **Dwa tryby (discriminator `content.view`):**
 *   - `list` - paginowana lista; każdy item to clickable card linkujący do
 *     pełnego artykułu (`item.href`)
 *   - `article` - pojedynczy news z opisem na listę + akapitami (z mediami
 *     per akapit) + button'em powrotu do listy (`list_href`).
 *
 * **Po cleanup 2026-06-04:** brak osobnej galerii artykułu i osobnych załączników -
 * wszystkie media (zdjęcia, pliki) siedzą w akapitach przez
 * `<ParagraphRenderer>`. Article view pokazuje tylko top-level info + paragraphy.
 *
 * **Security:** `short_text` może zawierać HTML z admin panel - trusted source,
 * OK dla `dangerouslySetInnerHTML`.
 */
export function CNews({ content }: CNewsProps) {
  const t = useT(cNewsTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyData}</div>
      </div>
    );
  }

  /* `hide_date` z config podstrony c_news - ukrywa datę publikacji w obu widokach. */
  const hideDate = !!content.config?.hide_date;

  return (
    <div className={styles.root}>
      {content.view === "article" && content.article ? (
        <ArticleView
          article={content.article}
          listHref={content.list_href ?? null}
          hideDate={hideDate}
          t={t}
        />
      ) : (
        <ListView content={content} hideDate={hideDate} t={t} />
      )}
    </div>
  );
}

export type CNewsProps = {
  content: CNewsContent;
};

/* ─── List view ─────────────────────────────────────────────────────────── */

function ListView({
  content,
  hideDate,
  t,
}: {
  content: CNewsContent;
  hideDate: boolean;
  t: CNewsTranslations;
}) {
  const items: CNewsItem[] = Array.isArray(content.items) ? content.items : [];
  const pagination = content.pagination;

  if (items.length === 0) {
    return <div className={styles.empty}>{t.emptyList}</div>;
  }

  return (
    <CatalogLoadMore<CNewsItem>
      initialItems={items}
      initialPage={pagination?.page ?? 1}
      totalPages={pagination?.total_pages ?? 1}
      totalCount={pagination?.total_count}
      getKey={(item) => item.id}
      extractItems={(c) => {
        const arr = (c as CNewsContent | null)?.items;
        return Array.isArray(arr) ? arr : [];
      }}
    >
      {(allItems) => (
        <ul className={styles.list}>
          {allItems.map((item) => (
            <NewsCard key={item.id} item={item} hideDate={hideDate} t={t} />
          ))}
        </ul>
      )}
    </CatalogLoadMore>
  );
}

function NewsCard({
  item,
  hideDate,
  t,
}: {
  item: CNewsItem;
  hideDate: boolean;
  t: CNewsTranslations;
}) {
  const href = extractInternalHref(item.href);

  const cardInner = (
    <>
      {item.picture && (
        <div className={styles.thumbWrap}>
          {/* Native <img> - `picture.thumb_src` jest URL'em do plików z
              Imperial backend'u (`/data/domains/.../images/m_thumb.jpg`).
              Brak Next/Image bo:
                - dynamic source (różne formaty/rozmiary per news)
                - cross-origin (backend = inna domena niż React-CMS)
                - Imperial już generuje thumbnail'e w wielu rozmiarach. */}
          <img
            src={item.picture.thumb_src}
            alt={item.name}
            width={item.picture.width ?? undefined}
            height={item.picture.height ?? undefined}
            className={styles.thumb}
            loading="lazy"
          />
        </div>
      )}
      <div className={styles.body}>
        {!hideDate && item.publication_date && (
          <time className={styles.date} dateTime={item.publication_date}>
            {t.formatDate(item.publication_date)}
          </time>
        )}
        <h2 className={styles.title}>{item.name}</h2>
        {/* `<div>` zamiast `<p>` - backend zwraca `short_text` z HTML
            (np. `<p>...</p>` z admin panel'a). Wrapping w `<p>` daje
            invalid nested paragraphs -> hydration mismatch (browser zamyka
            outer `<p>` przed inner). Block-level wrapper jest bezpieczny. */}
        <div
          className={styles.short}
          dangerouslySetInnerHTML={{ __html: item.short_text }}
        />
        <span className={styles.more}>{t.readMore}</span>
      </div>
    </>
  );

  return (
    <li className={styles.card}>
      {href !== null ? (
        <Link
          href={href}
          className={styles.cardLink}
          aria-label={item.name}
        >
          {cardInner}
        </Link>
      ) : (
        <a href={item.href} className={styles.cardLink} aria-label={item.name}>
          {cardInner}
        </a>
      )}
    </li>
  );
}

/* ─── Article view ─────────────────────────────────────────────────────── */

function ArticleView({
  article,
  listHref,
  hideDate,
  t,
}: {
  article: CNewsArticle;
  listHref: string | null;
  hideDate: boolean;
  t: CNewsTranslations;
}) {
  const backHref = listHref ? extractInternalHref(listHref) : null;

  return (
    <article className={styles.article}>
      {(backHref !== null || listHref) && (
        <div className={styles.articleBack}>
          {backHref !== null ? (
            <Link href={backHref} className={styles.articleBackLink}>
              {t.backToList}
            </Link>
          ) : (
            <a href={listHref!} className={styles.articleBackLink}>
              {t.backToList}
            </a>
          )}
        </div>
      )}

      <header className={styles.articleHeader}>
        <h2 className={styles.articleTitle}>{article.name}</h2>
        {!hideDate && article.publication_date && (
          <time className={styles.articleDate} dateTime={article.publication_date}>
            {t.formatDate(article.publication_date)}
          </time>
        )}
      </header>

      {article.list_image && (
        <figure className={styles.articleMainImage}>
          <img
            src={article.list_image.full_src}
            alt={article.name}
            width={article.list_image.width ?? undefined}
            height={article.list_image.height ?? undefined}
            loading="eager"
          />
        </figure>
      )}

      {article.short_text && (
        <div
          className={styles.articleLead}
          dangerouslySetInnerHTML={{ __html: article.short_text }}
        />
      )}

      {/* Pełna treść artykułu - akapity z imp_paragraph (każdy z własnymi
          obrazami/plikami via ParagraphRenderer). Po cleanup 2026-06-04
          ParagraphRenderer to jedyne źródło zawartości artykułu (galeria
          i pliki top-level usunięte razem z c_news_image/c_news_file). */}
      {article.paragraphs && article.paragraphs.length > 0 && (
        <ParagraphRenderer paragraphs={article.paragraphs} />
      )}

      {/* Reakcje + komentarze (biblioteka CMS Interakcje) - lazy, pod treścią
          artykułu. Reakcje renderowane nad komentarzami. */}
      {article.interactions &&
        (article.interactions.reactions?.enabled ||
          article.interactions.comments?.enabled) && (
          <ArticleInteractions interactions={article.interactions} />
        )}
    </article>
  );
}
